'use client';

import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useImperativeHandle,
  forwardRef,
  useCallback,
} from 'react';
import { RefreshCw } from 'lucide-react';

export interface CaptchaHandle {
  verifyCaptcha: (input: string) => boolean;
  generateCaptcha: () => void;
}

interface CaptchaProps {
  onVerify: (isVerified: boolean) => void;
  className?: string;
}

const Captcha = forwardRef<CaptchaHandle, CaptchaProps>(
  ({ onVerify, className = '' }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const onVerifyRef = useRef(onVerify);
    const [captchaText, setCaptchaText] = useState('');

    onVerifyRef.current = onVerify;

    const drawCaptcha = useCallback((text: string) => {
      const canvas = canvasRef.current;
      if (!canvas || !text) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      ctx.fillStyle = '#f0f0f0';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      for (let i = 0; i < 5; i++) {
        ctx.beginPath();
        ctx.moveTo(0, Math.random() * canvas.height);
        ctx.lineTo(canvas.width, Math.random() * canvas.height);
        ctx.strokeStyle = `rgba(${Math.random() * 100}, ${Math.random() * 100}, ${Math.random() * 100}, 0.5)`;
        ctx.stroke();
      }

      for (let i = 0; i < 50; i++) {
        ctx.fillStyle = `rgba(${Math.random() * 200}, ${Math.random() * 200}, ${Math.random() * 200}, 0.5)`;
        ctx.fillRect(
          Math.random() * canvas.width,
          Math.random() * canvas.height,
          2,
          2
        );
      }

      ctx.font = 'bold 24px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      for (let i = 0; i < text.length; i++) {
        const char = text.charAt(i);
        const x = 20 + i * 25;
        const y = canvas.height / 2 + Math.random() * 10 - 5;

        ctx.save();
        ctx.translate(x, y);
        ctx.rotate((Math.random() - 0.5) * 0.4);
        ctx.fillStyle = `rgb(${Math.floor(Math.random() * 80)}, ${Math.floor(Math.random() * 80)}, ${Math.floor(Math.random() * 80)})`;
        ctx.fillText(char, 0, 0);
        ctx.restore();
      }
    }, []);

    const generateCaptcha = useCallback(() => {
      const chars =
        '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
      let captcha = '';
      for (let i = 0; i < 5; i++) {
        captcha += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      setCaptchaText(captcha);
      onVerifyRef.current(false);
    }, []);

    useEffect(() => {
      generateCaptcha();
    }, [generateCaptcha]);

    useLayoutEffect(() => {
      if (!captchaText) return;
      drawCaptcha(captchaText);
    }, [captchaText, drawCaptcha]);

    const verifyCaptcha = useCallback(
      (input: string) => {
        if (!captchaText) {
          onVerifyRef.current(false);
          return false;
        }

        if (input.trim().toLowerCase() === captchaText.toLowerCase()) {
          onVerifyRef.current(true);
          return true;
        }

        generateCaptcha();
        return false;
      },
      [captchaText, generateCaptcha]
    );

    useImperativeHandle(ref, () => ({
      verifyCaptcha,
      generateCaptcha,
    }));

    return (
      <div
        className={`flex items-center justify-between gap-2 w-full min-h-[40px] ${className}`}
      >
        <canvas
          ref={canvasRef}
          width={144}
          height={40}
          className="border border-gray-300 rounded shrink-0 bg-[#f0f0f0]"
          aria-label="Captcha зураг"
        />

        <button
          className="flex items-center justify-center gap-1 w-10 h-10 shrink-0"
          onClick={generateCaptcha}
          type="button"
          aria-label="Captcha шинэчлэх"
        >
          <RefreshCw size={24} />
        </button>
      </div>
    );
  }
);

Captcha.displayName = 'Captcha';

export default Captcha;
